'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@chakra-ui/react'
import { Location, Region } from '@leid/api-locations'
import { Manager } from '@leid/api-managers'
import { ProductAvailabilityInfo } from '@leid/api-orders'

import { CustomerInfo } from 'app/utils/authentication'
import { getDistanceCoordinates } from 'app/utils/distance'
import { SortingInfo } from 'appTypes/mapper'
import { CartFormValues, OrderItemInfo } from 'appTypes/orders'
import { CheckPeriodPredicate } from 'components/form/DateTimePicker/interfaces'

import {
  findSelectedLocation,
  getAvailableLocations,
  getChainBelowRegionIds,
  getOriginalAvailableLocations,
  getUnavailableLocations,
} from './utils/locationsHelpers'
import { AvailabilityInfo } from './availabilityInfo'
import RegionsLocationsSection from './RegionsLocationsSection'
import { sortByDistance } from 'utils/sortItems'

export type RegionWithAvailability = Region & {
  hasAvailableLocations: boolean | undefined
}

type Props = {
  productLocations: Location[]
  isProlonging: boolean
  checkIfCorrectPeriod: CheckPeriodPredicate
  checkIfRerent: (locationId: number | undefined) => boolean
  values: CartFormValues
  onRegionLocationChange: (selectedRegion: Region | undefined, selectedLocationId: Location | undefined) => void
  setIsRerent: (isRerent: boolean) => void
  setNextIsDisabled: (disabled: boolean) => void
  setRegionHasAvailableLocations: (hasItems: boolean | undefined) => void
  orderItem: OrderItemInfo | undefined
  productCalendarList: ProductAvailabilityInfo
  customerInfo: CustomerInfo
  regions: Region[]
  manager: Manager | undefined
  sortingInfo: SortingInfo | undefined
  setSortingInfo: (sortingINfo: SortingInfo | undefined) => void
}

const LocationSelector = ({
  productLocations,
  isProlonging,
  checkIfCorrectPeriod,
  checkIfRerent,
  values,
  onRegionLocationChange,
  setIsRerent,
  setNextIsDisabled,
  setRegionHasAvailableLocations,
  productCalendarList,
  customerInfo,
  regions,
  manager,
  sortingInfo,
  setSortingInfo,
}: Props): JSX.Element => {
  const { t } = useTranslation(['locations'])

  const handleRegionChange = useCallback(
    (newRegion: Region | undefined) => {
      const newLocation = getFirstAvailableLocation(newRegion?.id)
      onRegionLocationChange(newRegion, newLocation)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRegionLocationChange],
  )

  const handleLocationChange = (newLocation: Location | undefined) => {
    let selectedRegion = originalRegions.find((s) => s.id === values.selectedRegionId)

    if (!newLocation) {
      onRegionLocationChange(selectedRegion, undefined)
      return
    }

    const selectedLocation = productLocations.find((s) => s.id === newLocation?.id)
    if (!selectedLocation) {
      onRegionLocationChange(selectedRegion, undefined)
      return
    }

    if (selectedLocation.regionId && values.selectedRegionId) {
      const selectedRegionsChain = getChainBelowRegionIds(values.selectedRegionId, originalRegions)

      // If selected location region is different from selected region chain - change region
      if (!selectedRegionsChain.some((s) => s === selectedLocation.regionId)) {
        const region = originalRegions.find((s) => s.id === selectedLocation.regionId)
        if (region) {
          selectedRegion = region
        }
      }
    }

    onRegionLocationChange(selectedRegion, selectedLocation)
  }

  const getRegionsWithAvailability = (locationsAvailability: AvailabilityInfo[]) => {
    const result: RegionWithAvailability[] = []

    const coordinates = getDistanceCoordinates(sortingInfo, customerInfo)
    const sortedRegions = sortByDistance(
      { long: coordinates?.longitudeCoordinate, lat: coordinates?.latitudeCoordinate },
      regions,
    )

    // Populate region availability info
    for (const region of sortedRegions) {
      let availableLocations = getAvailableLocations(region.id, locationsAvailability, regions)
      let notAvailableLocations = getUnavailableLocations(region.id, locationsAvailability, regions)

      if (values.managerId) {
        availableLocations = availableLocations.filter((s) => s.managerId === values.managerId)
        notAvailableLocations = notAvailableLocations.filter((s) => s.managerId === values.managerId)
      }

      result.push({
        ...region,
        hasAvailableLocations:
          availableLocations.length === 0 && notAvailableLocations.length === 0
            ? undefined // No locations propose product at region at all
            : availableLocations.length !== 0,
      })
    }

    return result
  }

  const getLocationsAvailabilityInfo = (productLocations: Location[]): AvailabilityInfo[] => {
    return productLocations.map((s) => {
      const correct = checkIfCorrectPeriod(s.id)
      const isRerent = checkIfRerent(s.id)
      return { correct, isRerent, location: s }
    })
  }

  const getFirstAvailableLocation = (regionId: number | undefined): Location | undefined => {
    if (regionId === undefined) {
      return undefined
    }

    const availabilityInfo = getLocationsAvailabilityInfo(originalAvailableLocations)
    const availableLocations = getAvailableLocations(regionId, availabilityInfo, originalRegions)
    const notAvailableLocations = getUnavailableLocations(regionId, availabilityInfo, originalRegions)

    if (availableLocations.length !== 0) {
      let location: Location | undefined
      if (!values.managerId || availableLocations.length === 1) {
        location = availableLocations[0]
      } else {
        const managerLocation = availableLocations.find((s) => s.managerId === values.managerId)
        location = managerLocation ?? availableLocations[0]
      }

      return location
    } else if (notAvailableLocations.length !== 0) {
      let location: Location | undefined
      if (!values.managerId || notAvailableLocations.length === 1) {
        location = notAvailableLocations[0]
      } else {
        const managerLocation = notAvailableLocations.find((s) => s.managerId === values.managerId)
        location = managerLocation ?? notAvailableLocations[0]
      }

      return location
    }

    return undefined
  }

  const getSelectedRegionId = (values: CartFormValues) => {
    if (!values.endDate || !values.returnAt || !values.pickupAt || !values.startDate) {
      return
    }

    let selectedRegionId = values.selectedRegionId

    if (!selectedRegionId && values.selectedLocationId) {
      const location = productLocations.find((s) => s.id === values.selectedLocationId)
      if (location && location.regionId) {
        const region = originalRegions.find((s) => s.id === location.regionId)
        if (region) {
          onRegionLocationChange(region, location)
          selectedRegionId = region.id
        }
      }
    }

    if (!selectedRegionId && !values.selectedLocationId) {
      const region = originalRegions.find((s) => s.hasAvailableLocations === true)
      if (region) {
        const firstLocation = getFirstAvailableLocation(region?.id)
        onRegionLocationChange(region, firstLocation)
        selectedRegionId = region?.id
      }
    }

    return selectedRegionId
  }

  const getAvailableLocationsInfo = (
    values: CartFormValues,
    locationsAvailability: AvailabilityInfo[],
  ): { availableLocations: Location[]; notAvailableLocations: Location[] } => {
    let availableLocations = getAvailableLocations(values.selectedRegionId, locationsAvailability, originalRegions)
    let notAvailableLocations = getUnavailableLocations(values.selectedRegionId, locationsAvailability, originalRegions)

    if (values.selectedLocationId) {
      const selectedLocation = findSelectedLocation(
        values.selectedLocationId,
        values.selectedRegionId,
        locationsAvailability,
        originalRegions,
      )
      if (selectedLocation) {
        if (selectedLocation.correct) {
          if (!selectedLocation.isRerent) {
            availableLocations = availableLocations.filter((s) => s.id !== values.selectedLocationId)
            availableLocations.unshift(selectedLocation?.location)
          }
        } else {
          notAvailableLocations = notAvailableLocations.filter((s) => s.id !== values.selectedLocationId)
          notAvailableLocations.unshift(selectedLocation?.location)
        }
      }
    }

    if (values.managerId) {
      availableLocations = availableLocations.filter((s) => s.managerId === values.managerId)
      notAvailableLocations = notAvailableLocations.filter((s) => s.managerId === values.managerId)
    }

    return { availableLocations, notAvailableLocations }
  }

  const getManagerAvailableLocationsInfo = (
    values: CartFormValues,
    locationsAvailability: AvailabilityInfo[],
    originalRegions: RegionWithAvailability[],
  ): { availableLocations: Location[]; notAvailableLocations: Location[] } => {
    if (!values.managerId) {
      return { availableLocations: [], notAvailableLocations: [] }
    }

    const allUnavailableLocations = getUnavailableLocations(undefined, locationsAvailability, originalRegions)
    const managerLocations = productLocations.filter((s) => s.managerId === values.managerId)
    const managerNotAvailableLocations = allUnavailableLocations.filter((s) =>
      managerLocations.find((m) => m.id === s.id),
    )
    const managerAvailableLocations = managerLocations.filter(
      (s) => !managerNotAvailableLocations.find((m) => m.id === s.id),
    )

    if (
      !values.selectedLocationId &&
      values.startDate &&
      values.pickupAt &&
      values.endDate &&
      values.returnAt &&
      managerAvailableLocations.length !== 0
    ) {
      const location = managerAvailableLocations[0]
      handleLocationChange(location)
    }

    return { availableLocations: managerAvailableLocations, notAvailableLocations: managerNotAvailableLocations }
  }

  const setNextButtonState = (
    values: CartFormValues,
    availableLocations: Location[],
    managerAvailableLocations: Location[],
    originalRegions: RegionWithAvailability[],
  ) => {
    if (!values.startDate || !values.pickupAt || !values.endDate || !values.returnAt || !values.selectedRegionId) {
      // There's not enough data yet selected - button disabled, not re-rent
      setNextIsDisabled(true)
      setRegionHasAvailableLocations(undefined)
      setIsRerent(false)
      return
    }

    if (values.selectedLocationId) {
      const allAvailableLocations = [...availableLocations, ...managerAvailableLocations]
      const location = allAvailableLocations.find((s) => s.id === values.selectedLocationId)
      if (location) {
        // Location is selected, available - button enabled, not re-rent
        setNextIsDisabled(false)
        setRegionHasAvailableLocations(undefined)
        setIsRerent(false)
        return
      }
    }

    const region = originalRegions.find((s) => s.id === values.selectedRegionId)
    if (!region) {
      // Wrong region
      setNextIsDisabled(true)
      setRegionHasAvailableLocations(undefined)
      setIsRerent(false)
      return
    }

    // Else - button is enabled, re-rent
    setNextIsDisabled(false)
    setRegionHasAvailableLocations(region.hasAvailableLocations)
    if (!isProlonging) {
      setIsRerent(true)
    }
    return
  }

  const originalAvailableLocations = useMemo(
    () => getOriginalAvailableLocations(productLocations, productCalendarList),
    [productLocations, productCalendarList],
  )

  const locationsAvailability = useMemo(
    () => getLocationsAvailabilityInfo(originalAvailableLocations),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.startDate, values.pickupAt, values.endDate, values.returnAt, values.amount, originalAvailableLocations],
  )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const originalRegions = useMemo(
    () => getRegionsWithAvailability(locationsAvailability),
    [locationsAvailability, sortingInfo, customerInfo, values.selectedRegionId, values.selectedLocationId],
  )

  useEffect(() => {
    getSelectedRegionId(values)
  }, [
    values.selectedRegionId,
    values.selectedLocationId,
    sortingInfo,
    values.endDate,
    values.returnAt,
    values.pickupAt,
    values.startDate,
  ])

  // const selectedRegionId = getSelectedRegionId(values)
  const { availableLocations, notAvailableLocations } = getAvailableLocationsInfo(values, locationsAvailability)
  const { availableLocations: managerAvailableLocations, notAvailableLocations: managerNotAvailableLocations } =
    getManagerAvailableLocationsInfo(values, locationsAvailability, originalRegions)

  // setNextButtonState(values, availableLocations, managerAvailableLocations, originalRegions)
  useEffect(() => {
    setNextButtonState(values, availableLocations, managerAvailableLocations, originalRegions)
  }, [
    values.startDate,
    values.pickupAt,
    values.endDate,
    values.returnAt,
    values.selectedRegionId,
    values.selectedLocationId,
  ])

  if (isProlonging) {
    return <></>
  }

  return (
    <Box>
      <Box paddingRight="40px">
        <Text fontSize="md" fontWeight="bold">
          {t('chooseLocation')}
        </Text>
      </Box>

      <RegionsLocationsSection
        regions={originalRegions}
        availableLocations={availableLocations}
        notAvailableLocations={notAvailableLocations}
        manager={manager}
        managerAvailableLocations={managerAvailableLocations}
        managerNotAvailableLocations={managerNotAvailableLocations}
        selectedRegionId={values.selectedRegionId}
        selectedLocationId={values.selectedLocationId}
        onLocationChange={handleLocationChange}
        onRegionChange={handleRegionChange}
        customerInfo={customerInfo}
        sortingInfo={sortingInfo}
        setSortingInfo={setSortingInfo}
      />

      {!values.selectedLocationId && !values.selectedRegionId && (
        <Box width="100%" textAlign="center" marginTop={5} marginBottom={5}>
          <Text fontSize="sm" color="green.a150" fontWeight={600}>
            {t('selectLocation')}
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default LocationSelector
